/**
 * The runtime type registry.
 *
 * Types are no longer a fixed table: people can change how long the built-in ones
 * keep things, and add their own. The built-ins remain the seed, and each keeps the
 * written rationale it shipped with — a user overriding "Notes & boards" to 90 days
 * is making an informed choice, but the reason the default was 30 should not vanish.
 *
 * One rule is not negotiable: Keep never gains an expiry. It exists so receipts,
 * tax records and dispute evidence have somewhere safe to sit, and a product that
 * lets that be edited into a countdown has a hole where its promise should be.
 */

import { PRESETS, describeLifetime, expiryFromLifetime, type Lifetime } from '@mayfly/core';
import { meta, setMeta } from './store.ts';

export interface TypeDef {
  id: string;
  label: string;
  lifetime: Lifetime;
  /** Why the default is what it is. Built-ins only. */
  rationale?: string;
  builtIn: boolean;
}

interface StoredConfig {
  /** Overrides keyed by built-in id: label and lifetime only. */
  overrides: Record<string, { label?: string; lifetime?: Lifetime }>;
  custom: { id: string; label: string; lifetime: Lifetime }[];
  /** Built-in ids the user has hidden. Keep can never be hidden. */
  hidden: string[];
}

const EMPTY: StoredConfig = { overrides: {}, custom: [], hidden: [] };
export const PROTECTED_ID = 'keep';

async function config(): Promise<StoredConfig> {
  return { ...EMPTY, ...(await meta<StoredConfig>('types')) };
}

export async function listTypes(): Promise<TypeDef[]> {
  const cfg = await config();
  const builtIns = PRESETS
    .filter((p) => p.id === PROTECTED_ID || !cfg.hidden.includes(p.id))
    .map((p): TypeDef => {
      const o = cfg.overrides[p.id] ?? {};
      return {
        id: p.id,
        label: o.label ?? p.label,
        // Keep's lifetime is never overridable, whatever is in storage.
        lifetime: p.id === PROTECTED_ID ? null : (o.lifetime !== undefined ? o.lifetime : p.lifetime),
        rationale: p.rationale,
        builtIn: true,
      };
    });
  const custom = cfg.custom.map((c): TypeDef => ({ ...c, builtIn: false }));
  // Custom types sit above Keep, which stays last as the deliberate escape hatch.
  const keep = builtIns.filter((t) => t.id === PROTECTED_ID);
  const rest = builtIns.filter((t) => t.id !== PROTECTED_ID);
  return [...rest, ...custom, ...keep];
}

export async function getType(id: string): Promise<TypeDef | undefined> {
  return (await listTypes()).find((t) => t.id === id);
}

export function consequenceOf(type: TypeDef): string {
  return describeLifetime(type.lifetime);
}

export async function expiryFor(id: string, nowMs: number, utcOffsetMin: number): Promise<number | null> {
  const type = await getType(id);
  // A missing type falls back to a day. A type whose lifetime IS null means Keep,
  // and must stay null — `type?.lifetime ?? fallback` silently turned Keep into a
  // 24-hour countdown, which is the exact failure Keep exists to prevent.
  const lifetime: Lifetime = type ? type.lifetime : { kind: 'duration', ms: 86_400_000 };
  return expiryFromLifetime(lifetime, nowMs, utcOffsetMin);
}

/* ------------------------------------------------------------------ editing */

export async function setLifetime(id: string, lifetime: Lifetime): Promise<void> {
  if (id === PROTECTED_ID) return; // Keep has no expiry, by design.
  const cfg = await config();
  const custom = cfg.custom.find((c) => c.id === id);
  if (custom) custom.lifetime = lifetime;
  else cfg.overrides[id] = { ...cfg.overrides[id], lifetime };
  await setMeta('types', cfg);
}

export async function setLabel(id: string, label: string): Promise<void> {
  const trimmed = label.trim().slice(0, 32);
  if (!trimmed) return;
  const cfg = await config();
  const custom = cfg.custom.find((c) => c.id === id);
  if (custom) custom.label = trimmed;
  else cfg.overrides[id] = { ...cfg.overrides[id], label: trimmed };
  await setMeta('types', cfg);
}

export async function addType(label: string, lifetime: Lifetime): Promise<string | null> {
  const trimmed = label.trim().slice(0, 32);
  if (!trimmed) return null;
  const cfg = await config();
  const id = `c_${trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-')}_${Date.now().toString(36)}`;
  cfg.custom.push({ id, label: trimmed, lifetime });
  await setMeta('types', cfg);
  return id;
}

/**
 * Removing a type never touches the photos filed under it — they keep their own
 * expiry, which was fixed when they were tagged. Deleting a label must not be a
 * way to accidentally delete evidence.
 */
export async function removeType(id: string): Promise<void> {
  if (id === PROTECTED_ID) return;
  const cfg = await config();
  cfg.custom = cfg.custom.filter((c) => c.id !== id);
  if (PRESETS.some((p) => p.id === id) && !cfg.hidden.includes(id)) cfg.hidden.push(id);
  await setMeta('types', cfg);
}

export async function resetTypes(): Promise<void> {
  await setMeta('types', EMPTY);
}

/* ------------------------------------------------------- duration helpers */

export const UNITS = [
  { id: 'hours', label: 'hours', ms: 3_600_000 },
  { id: 'days', label: 'days', ms: 86_400_000 },
] as const;

export function toDuration(value: number, unit: 'hours' | 'days'): Lifetime {
  const ms = Math.max(1, Math.min(3650, value)) * (unit === 'hours' ? 3_600_000 : 86_400_000);
  return { kind: 'duration', ms };
}

/** Splits a lifetime back into a number and a unit, for the editor. */
export function fromLifetime(l: Lifetime): { value: number; unit: 'hours' | 'days' | 'end-of-day' | 'never' } {
  if (l === null) return { value: 0, unit: 'never' };
  if (l.kind === 'end-of-day') return { value: 0, unit: 'end-of-day' };
  const hours = l.ms / 3_600_000;
  return hours % 24 === 0 && hours >= 24
    ? { value: hours / 24, unit: 'days' }
    : { value: Math.round(hours), unit: 'hours' };
}
